/* WJW 17: OPTIONAL, lazy card enrichment from the same-store Shopify product
   Ajax endpoint. Only same-variant compare-at and tier tags justify SAVE badges.
   Boost remains the recommendation-ranking source; pricing remains display-only.
   No network request blocks the initial card render. */
(() => {
  'use strict';
  const mobile = window.matchMedia('(max-width:749px)');
  const selector = '.wjw-boost-discovery__card, .wjw-collection-bestsellers__card';
  const cache = new Map();
  const queue = [];
  let active = 0;
  const maxConcurrent = 2;
  function tagsFrom(data) {
    if (Array.isArray(data.tags)) return data.tags;
    if (typeof data.tags === 'string') return data.tags.split(',').map(s => s.trim());
    return [];
  }
  function verifiedFactor(tags) {
    if (!tags.length || tags.some(t => ['Kit','nomarkdown','gift-card','Membership'].includes(t))) return null;
    const schedules = tags.filter(t => typeof t === 'string' && t.startsWith('discount_'));
    if (!schedules.length) return null; // Don't guess the missing tag's tier.
    let max = 0;
    for (const tag of schedules) {
      const rest = tag.slice(9);
      if (!rest) continue; // Bare discount_ is a grid without tier savings.
      const parts = rest.split('_');
      if (parts.length % 2) return null;
      for (let i = 0; i < parts.length; i += 2) {
        const qty = Number(parts[i]), pct = Number(parts[i+1]);
        if (!Number.isInteger(qty) || qty < 1 || !Number.isFinite(pct) || pct < 0 || pct > 100) return null;
        max = Math.max(max,pct);
      }
    }
    return (100 - max) / 100;
  }
  function money(cents, currency, locale) {
    return new Intl.NumberFormat(locale || 'en-US',{style:'currency',currency:currency}).format(cents/100);
  }
  function loadProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const promise = new Promise((resolve) => {
      queue.push({handle,resolve}); drain();
    });
    cache.set(handle,promise);
    return promise;
  }
  function drain() {
    if (!queue.length || active >= maxConcurrent) return;
    const task = queue.shift();
    active++;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const url = root.replace(/\/?$/,'/') + 'products/' + encodeURIComponent(task.handle) + '.js';
    fetch(url,{credentials:'same-origin',headers:{Accept:'application/json'}})
      .then(r => {if (!r.ok) throw Error('product unavailable'); return r.json();})
      .then(task.resolve,() => task.resolve(null))
      .finally(() => {active--; drain();});
  }
  function hydrate(card) {
    if (card.dataset.wjwPriceChecked) return;
    card.dataset.wjwPriceChecked = '1';
    const handle = card.querySelector('[data-product-handle]')?.dataset.productHandle;
    const section = card.closest('[data-wjw-boost-home],[data-wjw-collection-bestsellers],[data-wjw-boost-results]');
    if (!handle || !/^[a-z0-9-]+$/i.test(handle) || !section) return;
    if (section.dataset.currency !== section.dataset.shopCurrency) return;
    const currency = section.dataset.currency || 'USD';
    const locale = section.dataset.locale || 'en-US';
    loadProduct(handle).then(product => {
      if (!product || !card.isConnected || !mobile.matches) return;
      const advertisedId = String(card.dataset.productId || '').replace(/\D/g,'');
      if (advertisedId && String(product.id) !== advertisedId) return;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (!variants.length) return;
      const tags = tagsFrom(product);
      const isMembership = tags.some(t => String(t).trim().toLowerCase() === 'membership');
      card.classList.toggle('wjw59b14-membership', isMembership);
      if (isMembership) {
        card.querySelectorAll('.wjw-mobile-merch-badge, .badge.onsale').forEach(el => {
          if (/^(save|on sale)/i.test(el.textContent.trim())) el.remove();
        });
      }
      // Product-level promotion (any discounted variant), independent of the
      // verified lowest-variant SAVE badge calculation below.
      const anySale = variants.some(v => Number(v.price) > 0 &&
        Number(v.compare_at_price) > Number(v.price));
      card.classList.toggle('wjw59b6-any-sale',anySale && !isMembership);
      const variant = variants.reduce((a,b) => Number(b.price) < Number(a.price) ? b : a);
      const base = Number(variant.price); // Shopify product.js prices are integer cents.
      const compare = Number(variant.compare_at_price || 0);
      if (!Number.isInteger(base) || base < 0) return;
      const factor = verifiedFactor(tags);
      const media = card.querySelector('.wjw-boost-discovery__media-wrap,.wjw-collection-bestsellers__media-wrap');
      if (!media) return;
      const homeAmazon = !!card.closest('[data-wjw-boost-home],[data-wjw-boost-results]'); // Inc48 Shop All uses identical Amazon card pricing/merch rules.
      const priceEl = card.querySelector('.wjw-boost-discovery__price,.wjw-collection-bestsellers__price');
      if (homeAmazon) {
        // Only the homepage Amazon 2x2 opts into the established WJW Liquid
        // display-factor fallback. Existing collection/results badge logic
        // remains unchanged. Shopify's same-variant cents are authoritative.
        const displayFactor = window.WJWDiscountPricing?.factor(tags);
        if (priceEl && Number.isFinite(displayFactor)) {
          priceEl.replaceChildren();
          if (compare > base) {
            const former = document.createElement('del');
            former.textContent = money(compare,currency,locale);
            priceEl.append(former);
          }
          const actual = document.createElement('span');
          const low = Math.round(base * displayFactor);
          const noAsLowAs = tags.includes('gift-card') || tags.includes('Membership');
          if (!noAsLowAs) actual.append(document.createTextNode('As low as '));
          const amount = document.createElement('span');
          amount.className = 'wjw59b6-price-amount';
          amount.textContent = money(low,currency,locale);
          actual.append(amount);
          priceEl.append(actual);
        }
        // Match snippets/wjw-mobile-tag-statement.liquid exactly.
        const normalized = tags.map(t => String(t).trim().toLowerCase());
        const statement = normalized.includes('top 10% reordered') || normalized.includes('top-10-reordered')
          ? 'Top 10% reordered'
          : normalized.some(t => ['bestseller','best seller','best-seller'].includes(t)) ? 'Bestseller' : '';
        const merch = card.querySelector('.wjw-boost-discovery__merch-statement');
        if (merch) merch.textContent = statement;
      }
      if (variants.every(v => !v.available)) {
        const badge = document.createElement('span');
        badge.className = 'wjw-mobile-merch-badge wjw-mobile-merch-badge--soldout';
        badge.textContent = 'Back soon';
        media.prepend(badge);
        return;
      }
      if (factor === null) return;
      const lowCents = Math.round(base * factor);
      if (priceEl && !homeAmazon) {
        priceEl.replaceChildren();
        if (compare > base) {
          const former = document.createElement('del');
          former.textContent = money(compare,currency,locale);
          priceEl.append(former);
        }
        const actual = document.createElement('span');
        actual.append(document.createTextNode('As low as '));
        const amount = document.createElement('span');
        amount.className = 'wjw59b6-price-amount';
        amount.textContent = money(lowCents,currency,locale);
        actual.append(amount);
        priceEl.append(actual);
      }
      // Retain historical lowest-variant SAVE calculation if applicable.
      // Otherwise promote a REAL marked-down variant, never a cross-variant
      // compare-at/price pairing. Display-only; Shopify prices are untouched.
      let save = 0;
      if (compare > base && base > 0) {
        save = Math.round((compare - base * factor) * 100 / compare);
      } else {
        for (const saleVariant of variants) {
          const vPrice = Number(saleVariant.price);
          const vCompare = Number(saleVariant.compare_at_price || 0);
          if (vPrice > 0 && vCompare > vPrice) {
            const pct = Math.round((vCompare - vPrice * factor) * 100 / vCompare);
            if (pct > save && pct < 100) save = pct;
          }
        }
      }
      if (!isMembership && save > 0 && save < 100) {
        const badge = document.createElement('span');
        badge.className = 'wjw-mobile-merch-badge';
        badge.textContent = 'Save ' + save + '%';
        media.prepend(badge);
      }
    });
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) {observer.unobserve(entry.target); hydrate(entry.target);} });
  },{rootMargin:'200px 0px'}) : null;
  function collect(root) {
    if (!mobile.matches) return;
    (root || document).querySelectorAll(selector).forEach(card => {
      if (card.dataset.wjwPriceQueued) return;
      card.dataset.wjwPriceQueued = '1';
      if (observer) observer.observe(card); else hydrate(card);
    });
  }
  document.addEventListener('wjw:cards-inserted', event => collect(event.detail && event.detail.root));
  document.addEventListener('shopify:section:load',event => collect(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => collect(document));
  else collect(document);
})();
